import { API, graphqlOperation } from 'aws-amplify';
import { GraphQLResult } from '@aws-amplify/api';
import { v4 as uuid } from 'uuid';
import {
  CardDetailsInput,
  GetUrlQuery,
  TranscriptResultsInput,
} from '../graphql/api';
import { getUrl } from '../graphql/queries';
import {
  imageDimensions,
  ImageEditorValidation,
  ImageValidatorResponseType,
  SettingsData,
} from '../types';
import {
  FeedCard,
  GetUrlQueryType,
  Recording,
  SocialUrls,
} from '../graphql/types';

import * as Cache from './cache';
import axios from 'axios';
import AvatarEditor from 'react-avatar-editor';
import {
  setCardProps,
  setEditingChapterId,
  setGiideContentModal,
  setImageEditorState,
  setOldGiideData,
} from '../actions';
import { Dispatch } from 'redux';
import { message } from 'antd';
import ReactGA from 'react-ga';
import { RcFile } from 'antd/es/upload';
import { detect } from 'detect-browser';
import React from 'react';
import { batch } from 'react-redux';
import { HookAPI } from 'antd/es/modal/useModal';

export const handleErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error) {
    message.error(error.message);
  } else {
    message.error(fallback);
  }
};

export const sortUpdatedAt = (a, b) => {
  return b.updatedAt!.localeCompare(a.updatedAt as string);
};

export const sortCreatedAt = (a, b) => {
  return b.createdAt!.localeCompare(a.createdAt as string);
};

export const sortAlphabeticallyByKey = key => {
  return (a, b) => a[key].localeCompare(b[key]);
};

export const sortBooleanByKey = key => {
  return (a, b) => b[key] - a[key];
};

export const sortAlphabeticallyByName = (a, b) => {
  if (
    a &&
    a.speaker &&
    a.speaker.fullName &&
    b &&
    b.speaker &&
    b.speaker.fullName
  ) {
    return a.speaker.fullName.localeCompare(b.speaker.fullName);
  } else {
    return -1;
  }
};

export const handleInputChange =
  (set: (value: string) => void) =>
  (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    //console.log("e " + e) ;
    let validated = e.target.value;
    //if (validated) validated.trim();
    //if (validated === '') validated = null;
    set(validated);
  };

export const stopPropagation = f => e => {
  e.stopPropagation();
  f();
};

export const formInputLength = (input: string, maxLen: number) =>
  input.length <= maxLen ? input : input.slice(0, -1);

export const formatTime = (time: number, format: number): string =>
  ('00' + Math.floor(time)).substr(format);

export const timeToString = (seconds: number, hours = true) =>
  `${hours ? `${formatTime(seconds / 3600000, -2)}:` : ''}${formatTime(
    seconds / 60000,
    -2
  )}:${formatTime((seconds % 60000) / 1000, -2)}`;

export const milisecToString = (seconds: number): string =>
  '.' + formatTime(seconds % 60000, -3).substring(0, 2);

export const formatSeconds = (seconds: number) =>
  `${formatTime(seconds / 60, -2)}:${formatTime(seconds % 60, -2)}`;

export enum StepState {
  done,
  new,
  processing,
}

export const splitName = (fullName: string) => {
  const lastSpaceIndex = fullName.lastIndexOf(' ');
  const firstName =
    lastSpaceIndex > -1 ? fullName.substring(0, lastSpaceIndex) : '';
  const lastName = fullName.substring(lastSpaceIndex + 1);
  return [firstName, lastName];
};

export const createSpeakerHeadshotPath = (
  tenantId: string,
  giideId: string,
  filename: string
) => {
  const imagePath = `${tenantId}/${giideId}/assets/speaker/${uuid()}.${filename
    .split('.')
    .pop()}`;
  return imagePath;
};

export const getPathForUserImage = (
  tenantId: string,
  userId: string
): string => {
  return `${tenantId}/${userId!}`;
};

export const cleanUrl = (url: string): string => {
  return url.trim().replace(/^https:\/\//, '');
};

export const urlRegex =
  /^[a-z0-9]+([-.][a-z0-9]+)*\.[a-z]{2,63}(:[0-9]{1,5})?(\/.*)?$/;

export const urlWithoutHttps = url => {
  return url.replace(/^https:\/\//, '');
};

export const isUrlHttps = url => {
  return /^(https:\/\/)(.+)$/.test(url);
};

export const routerBaseName = '/studio/';

export const sortStepsByOrder = (a, b) => {
  return a.order - b.order;
};

export const imageMaxSize = 10; // in MB
export const fileMinSize = 1; // in KB

export const isFileLargerThanLimit = (fileSize, maxFileSize) => {
  return fileSize / 1000 / 1000 > maxFileSize;
};

export const getStepToRemoveIndex = (steps, stepId) => {
  let stepToRemoveIndex = -1;
  for (let i = 0; i < steps.length; i++) {
    if (steps[i].id === stepId) {
      stepToRemoveIndex = i;
      break;
    }
  }
  return stepToRemoveIndex;
};

export const reorderSteps = (indexFrom, indexTo, stepList) => {
  const newStepList = Array(stepList.length).fill(0);

  for (let i = 0; i < stepList.length; i++) {
    if (indexFrom < indexTo) {
      if (i < indexFrom) {
        newStepList[i] = stepList[i];
      } else if (i >= indexFrom && i < indexTo) {
        newStepList[i] = stepList[i + 1];
      } else if (i === indexTo) {
        newStepList[i] = stepList[indexFrom];
      } else {
        newStepList[i] = stepList[i];
      }
    } else {
      if (i < indexTo) {
        newStepList[i] = stepList[i];
      } else if (i === indexTo) {
        newStepList[i] = stepList[indexFrom];
      } else if (i > indexTo && i <= indexFrom) {
        newStepList[i] = stepList[i - 1];
      } else {
        newStepList[i] = stepList[i];
      }
    }
  }

  for (let i = 0; i < newStepList.length; i++) {
    newStepList[i].order = i;
  }

  return newStepList;
};

export const isFileSmallerThanLimit = (fileSize, minFileSize) => {
  return fileSize / 1000 < minFileSize;
};

export const defaultImageMinWidth = 100;
export const defaultImageMinHeight = 100;

export const getBase64Img = (img, callback) => {
  const reader = new FileReader();
  reader.addEventListener('load', () => callback(reader.result));
  reader.readAsDataURL(img);
};

export const getImgDimensionsPromise = file => {
  return new Promise<imageDimensions>((resolved, rejected) => {
    try {
      getBase64Img(file, base64Img => {
        const img = new Image();
        img.src = base64Img;
        img.onload = () => {
          resolved({ width: img.width, height: img.height });
        };
      });
    } catch {
      rejected(new Error('Failed to read img dimensions'));
    }
  });
};

export const checkImageAttributes = async (
  file: Blob | RcFile,
  checkOnlyDims: Boolean = false,
  minWidth?: number,
  minHeight?: number
): Promise<ImageValidatorResponseType> => {
  let result: ImageValidatorResponseType = { isValid: true, error: undefined };
  if (file === undefined) {
    result.error = 'Failed to load image file';
  } else {
    if (checkOnlyDims) {
      const imgDims: imageDimensions | null = await getImgDimensionsPromise(
        file
      ).catch(err => {
        result.error = err.message;
        return null;
      });
      if (
        imgDims &&
        (imgDims.width < defaultImageMinWidth ||
          imgDims.height < defaultImageMinHeight)
      ) {
        result.error = `Minimum image pixel size must be at least ${defaultImageMinWidth}x${defaultImageMinHeight}, current pixel size is ${imgDims.width}x${imgDims.height}.`;
      }
    } else {
      const fileMimeType = file.type.toLowerCase();
      const allowedMimeType = ['image/jpeg', 'image/png', 'image/gif'];
      const allowedExt = ['.jpg', '.png', '.jpeg', '.gif'];
      let ext;
      if ('name' in file) {
        ext = file.name.slice(file.name.lastIndexOf('.'));
      }

      //console.log('ext ' + ext);
      //console.log('fileMimeType ' + fileMimeType);

      if (
        allowedMimeType.includes(fileMimeType) &&
        ((ext && allowedExt.includes(ext.toLowerCase())) || !ext)
      ) {
        const isLargerThanLimit = isFileLargerThanLimit(
          file.size,
          imageMaxSize
        );
        const isSmallerThanLimit = isFileSmallerThanLimit(
          file.size,
          fileMinSize
        );
        if (isSmallerThanLimit) {
          result.error = `Minimum image size is ${fileMinSize} KB`;
        } else if (isLargerThanLimit) {
          result.error = `Maximum image size is ${imageMaxSize} MB`;
        } else {
          const imgDims: imageDimensions | null = await getImgDimensionsPromise(
            file
          ).catch(err => {
            result.error = err.message;
            return null;
          });

          const imageMinWidth = minWidth ? minWidth : defaultImageMinWidth;
          const imageMinHeight = minHeight ? minHeight : defaultImageMinHeight;

          if (
            imgDims &&
            (imgDims.width < imageMinWidth || imgDims.height < imageMinHeight)
          ) {
            result.error = `Minimum image pixel size must be at least
            ${imageMinWidth}x${imageMinHeight},
            current pixel size is ${imgDims.width}x${imgDims.height}.`;
          }
        }
      } else {
        result.error = 'Accepted file types: .jpg, .png, .gif';
      }
    }
  }
  result.isValid = !result.error;
  return result;
};

export const fileKeyForFeedCardImage = (
  tenantId: string,
  giideId: string,
  fileName: string,
  userId?: string
): string => {
  // optional parameter userId is used to generate uploading key to UPLOAD_BUCKET.
  // without userId it generate key for reading from RECORDINGS_BUCKET
  // the User ID should be passed when updating the user images table
  return `${tenantId}${
    userId ? `/${userId}` : ''
  }/${giideId}/assets/${fileName}`;
};

export const fileNameIdForFeedCardImage = (cardProps, fileName): string => {
  if (cardProps?.customImage) {
    // This is when the fileNameId already exists
    const urlParts = cardProps?.customImage.split('/');
    return urlParts[urlParts.length - 1];
  } else {
    // New card, no id yet
    return uuid();
  }
};

export const getAudioDuration = (data: string) => {
  return new Promise<number>((resolved, rejected) => {
    try {
      const audio = new Audio(data);
      const getDuration = () => {
        audio.currentTime = 0;
        audio.removeEventListener('timeupdate', getDuration);
        resolved(audio.duration);
      };
      audio.addEventListener('loadedmetadata', () => {
        // adding workaround for bug in chrome where
        // HTML5 audio/video duration is infinite
        if (audio.duration === Infinity) {
          audio.addEventListener('timeupdate', getDuration);
          audio.currentTime = 1e101;
        } else {
          resolved(audio.duration);
        }
      });
    } catch {
      rejected(new Error('Failed to read audio duration length'));
    }
  });
};

export const getAudioDuationFromAC = (data: ArrayBuffer) => {
  return new Promise<number>((resolved, rejected) => {
    try {
      const audioContext = new AudioContext();
      audioContext.decodeAudioData(data, buffer => {
        resolved(buffer.duration);
      });
    } catch {
      rejected(new Error('Failed to read audio duration length'));
    }
  });
};

export const getAudioDurationFromFile = (file: RcFile | File) => {
  return new Promise<number>(async (resolved, rejected) => {
    try {
      const reader = new FileReader();
      const browser = detect();
      if (browser?.name === 'firefox') {
        reader.onload = e => {
          if (e.target && e.target.result) {
            resolved(getAudioDuationFromAC(e.target.result as ArrayBuffer));
          }
        };
        reader.readAsArrayBuffer(file);
      } else {
        reader.onload = e => {
          if (e.target && e.target.result) {
            resolved(getAudioDuration(e.target.result as string));
          }
        };
        reader.readAsDataURL(file);
      }
    } catch {
      rejected(new Error('Failed to read audio duration length'));
    }
  });
};

export enum State {
  waiting = 'Waiting for Review',
  published = 'Published',
  unpublished = 'Draft',
  migrating = 'Migrating',
  transcoding = 'Transcoding',
  error = 'Error',
  review = 'Review',
  pending = 'Pending',
  public = 'Public',
  private = 'Private',
}

export const createThumbnailPath = (
  tenantId: string,
  userId: string,
  giideId: string,
  id?: string
) => {
  // const ext = filename.split('.').pop();
  id = id || uuid();

  return {
    s3Url: `${tenantId}/${userId}/${giideId}/publish/${id}`,
    customImagePath: `${tenantId}/${userId}/${giideId}/assets/${id}`,
    id,
  };
};

export const DEFAUTL_CARD_COLOR = 'blue';
export const DEFAULT_CARD_SHADE = 'medium';
export const DEFAULT_THUMBNAIL_ID = '01';
export const DEFAULT_THUMBNAIL_IMG = `${DEFAUTL_CARD_COLOR}_${DEFAULT_CARD_SHADE}_${DEFAULT_THUMBNAIL_ID}`;

export const ITEMS_PER_PAGE = 15;

export const RECORDINGS_BUCKET = process.env.REACT_APP_RECORDINGS_BUCKET;
export const UPLOAD_BUCKET = process.env.REACT_APP_UPLOAD_BUCKET;
export const AUDIO_LIBRARY_BUCKET = process.env.REACT_APP_LIBRARY_BUCKET;

export enum LibraryState {
  uploaded = 'Processing',
  finished = 'Ready',
  error = 'Error',
}

export const encodeUrl = (url: string | null | undefined) => {
  return url ? encodeURIComponent(url) : null;
};

export const encodeSocialUrls = (socialUrls: SocialUrls) => {
  if (socialUrls) {
    const encodedSocialUrls = Object.keys(socialUrls).map(key => {
      return [key, encodeUrl(socialUrls[key])];
    });
    return Object.fromEntries(encodedSocialUrls);
  }
  return null;
};

export const decodeUrl = (url: string | null | undefined) => {
  return url ? decodeURIComponent(url) : null;
};

export const decodeSocialUrls = (socialUrls: SocialUrls) => {
  if (socialUrls) {
    const decodedSocialUrls = Object.keys(socialUrls).map(key => {
      return [key, decodeUrl(socialUrls[key])];
    });
    return Object.fromEntries(decodedSocialUrls);
  }
  return null;
};

export const processRecoding = (recording: Recording) => {
  return new Promise(async (resolve, reject) => {
    if (recording) {
      if (recording.feedCards) {
        recording.feedCards.items =
          (recording.feedCards.items as FeedCard[]).map(
            (feedcard: FeedCard) => {
              if (feedcard) {
                const decodedSocialUrls = decodeSocialUrls(
                  feedcard.socialUrls as SocialUrls
                );
                feedcard.url = decodeUrl(feedcard.url);
                feedcard.imageUrl = decodeUrl(feedcard.imageUrl);
                feedcard.iconUrl = decodeUrl(feedcard.iconUrl);
                feedcard.socialUrls = decodedSocialUrls;
              }
              return feedcard;
            }
          ) || null;
      }

      if (recording.deleted || !recording.transcriptPath) {
        return resolve(recording);
      }
      const transcriptPath = recording.transcriptPath;
      let fileType = '';

      if (recording.transcriptError) {
        reject('transcriptError');
        return;
      }

      if (transcriptPath)
        fileType = transcriptPath.substr(transcriptPath.length - 8, 3);

      if (!recording.transcriptResults) {
        const transcriptFilePath = recording.transcriptPath
          ? recording.transcriptPath
          : `public/${recording.tenantID}/${recording.giideId}/${recording.id}.${fileType}.json`;
        const data = await getSignedUrl(transcriptFilePath).catch(() => {
          reject('Failed to load transcript results');
        });
        if (data) {
          const res = await fetch(data?.url as string);
          if (res && res.status === 200) {
            const downloadedJson = await res.blob();
            if (downloadedJson) {
              const transcriptJson = JSON.parse(await downloadedJson.text());
              const transcriptResults: TranscriptResultsInput = {
                items: transcriptJson.results.items,
              };
              if (transcriptJson.results.transcripts[0].transcript) {
                transcriptResults.transcript =
                  transcriptJson.results.transcripts[0].transcript;
              }
              recording.transcriptResults =
                transcriptResults as Recording['transcriptResults'];
            }
          }
        }
      }
    }
    resolve(recording);
  });
};

export enum SettingsTabKeys {
  details = 'details',
  image = 'image',
  preview = 'preview',
  audioTones = 'audioTones',
  permissions = 'permissions',
  settings = 'settings',
}

export enum SettingsTabText {
  details = 'Details',
  image = 'Cover Image',
  settings = 'Settings',
  preview = 'Preview',
  audioTones = 'Audio Tones',
  permissions = 'Permissions',
}

export const settingsTabArray = [
  SettingsTabKeys.details,
  SettingsTabKeys.image,
  // SettingsTabKeys.settings,
  SettingsTabKeys.preview,
  // SettingsTabKeys.permissions,
  // SettingsTabKeys.audioTones,
];

// export const CreationStepsArray = [
//   'Add Audio',
//   'Link Content',
//   'Preview & Publish',
// ];

export const CreationStepsArray = [
  {
    title: 'Add Audio',
    tooltip: {
      title: 'Add Audio',
      tips: [
        'Set yourself up for success by organizing your thoughts ahead of time.',
        'Locate yourself in a quiet room and speak clearly and slowly when you record.',
        "If you make a mistake, it's ok. That's part of the process, so just keep going!",
      ],
      links: [
        {
          title: 'Tips for recording',
          url: `${process.env.REACT_APP_HELP_GIIDE_1}`,
        },
        {
          title: 'Voice Confrontation',
          url: `${process.env.REACT_APP_HELP_GIIDE_2}`,
        },
        {
          title: 'Making mistakes in recording',
          url: `${process.env.REACT_APP_HELP_GIIDE_3}`,
        },
      ],
      helpQuestions: [
        {
          title: 'How long can giides be? File size recommendations?',
          url: `${process.env.REACT_APP_BASE_URL}/help/how-long-can-giides-be`,
        },
        {
          title: 'How do I add audio to my giide?',
          url: `${process.env.REACT_APP_BASE_URL}/help/how-to-add-audio`,
        },
      ],
    },
  },
  {
    title: 'Create Feed',
    tooltip: {
      title: 'Feed Creation',
      tips: [
        'Add a variety of feedcards to keep your audience engaged.',
        "Try to space your feedcards every 1-2 sentences so that you don't overwhelm your listeners.",
        "Make sure your feedcard images complement your audio. Match the timing of the words you're saying with the resources that pop up in your feed.",
      ],
      links: [
        {
          title: 'Tips for linking',
          url: `${process.env.REACT_APP_HELP_GIIDE_4}`,
        },
      ],
      helpQuestions: [
        {
          title: 'How do I upload images to Giide? File size recommendations?',
          url: `${process.env.REACT_APP_BASE_URL}/help/how-to-upload-images`,
        },
        {
          title: 'Can I edit the audio transcription of my giide?',
          url: `${process.env.REACT_APP_BASE_URL}/help/edit-audio-transcription`,
        },
      ],
    },
  },
  {
    title: 'Share',
    tooltip: {
      title: 'Share',
      tips: [
        'Publishing a giide simply creates a URL you can then share on social media or send via email or text.',
        'To publish your giide, go through the publishing flow.',
      ],
      links: [
        {
          title: 'Tips for sharing',
          url: `${process.env.REACT_APP_HELP_GIIDE_5}`,
        },
      ],
      helpQuestions: [
        {
          title: 'What happens when I publish a giide?',
          url: `${process.env.REACT_APP_BASE_URL}/help/what-happens-on-publish`,
        },
        {
          title: 'How do I share a giide?',
          url: `${process.env.REACT_APP_BASE_URL}/help/how-to-share`,
        },
        {
          title: "How do I edit a giide once it's published?",
          url: `${process.env.REACT_APP_BASE_URL}/help/how-to-edit`,
        },
        {
          title: 'Does using Giide give me SEO benefits the way a blog might?',
          url: `${process.env.REACT_APP_BASE_URL}/help/giide-seo-benefits`,
        },
      ],
    },
  },
];

export const getBlobFromAvatarImage = async editorRef => {
  // Renders the current state of an avatar editor image

  if (!editorRef) {
    return;
  }

  try {
    const img = editorRef.getImage().toDataURL();
    const response = await fetch(img);
    const blob = await response.blob();

    const imgAttr = await checkImageAttributes(blob, true);

    if (!imgAttr.isValid) {
      throw new Error(imgAttr.error);
    }
    // TODO Do validation

    return blob;
  } catch (err) {
    if (err instanceof Error) {
      throw err;
    } else {
      throw new Error('Error getting blob from avatar image.');
    }
  }
};

export const handleImageInEditor = async (
  editorRef: AvatarEditor | null,
  imageMimeType: string,
  dispatch?: Dispatch,
  uploadImage?: (file, returnData?: boolean) => Promise<boolean> | Promise<{}>
): Promise<CardDetailsInput> => {
  let data;

  if (editorRef) {
    if (dispatch) {
      dispatch(
        setImageEditorState({
          position: { x: editorRef.state.image.x, y: editorRef.state.image.y },
          scale: editorRef.props.scale,
        })
      );
    }

    let img: string | Blob;

    if (imageMimeType === 'image/jpeg') {
      img = editorRef.getImage().toDataURL('image/jpeg', 0.92);
    } else {
      img = editorRef.getImage().toDataURL();
    }

    await fetch(img)
      .then(res => res.blob())
      .then(blob => (img = blob));

    if (uploadImage) {
      data = await uploadImage(img, true);
    } else {
      const imageValidation: ImageValidatorResponseType =
        await checkImageAttributes(img as unknown as Blob, true);

      if (!imageValidation.isValid) {
        message.error(imageValidation.error);
        data = imageValidation.error;
      } else {
        data = img;
      }
    }
  }

  return data;
};

export const defaultImageEditorProps = {
  image: null,
  fileName: 'unnamed_file',
  imgWidth: 134,
  imgHeight: 134,
  imgBorderWidth: 92,
  imgBorderHeight: 79.5,
  imgBorderRadius: 8,
  sliderWidth: 152.75,
  removeTextClass: 'defaultRemoveImageText',
  boxSizeClass: 'defaultImgEditorBoxSize',
  staticImageClass: 'defaultStaticImageSquare',
};

export type IGetSignedUrl = (
  fileKey: string,
  bucket?: string | null | undefined,
  resourceType?: 'asset' | 'userRecording' | 'headshot',
  type?: 'get' | 'post',
  imageService?: string | null | undefined
) => Promise<GetUrlQueryType>;

export const getSignedUrl: IGetSignedUrl = async (
  fileKey,
  bucket = RECORDINGS_BUCKET,
  resourceType = 'asset',
  type = 'get',
  service
) => {
  try {
    const cacheKey = `${fileKey}-${type}`;
    if (
      type === 'post' ||
      !Cache.has(cacheKey) ||
      Cache.isExpired(cacheKey, 60 * 60)
    ) {
      const signedUrlData = (await API.graphql(
        graphqlOperation(getUrl, {
          input: { fileKey, type, bucket, resourceType, service },
        })
      )) as GraphQLResult<GetUrlQuery>;
      const data = signedUrlData.data?.getUrl;
      if (data) {
        const { fields, url, success, error } = data;
        if (error) {
          throw new Error('failed to sign the request');
        }
        if (url && error === null) {
          const cacheData: GetUrlQueryType = { url, success, error };
          if (fields) {
            const parsedFields = JSON.parse(fields);
            const keys = Object.keys(parsedFields);
            const formData = keys.reduce((fd, val, i) => {
              fd.append(val, parsedFields[val]);
              return fd;
            }, new FormData());
            cacheData.fields = formData;
          }
          Cache.set(cacheKey, cacheData);
        }
      }
    }

    const cacheItem = Cache.get(cacheKey);
    if (!cacheItem) {
      throw new Error('failed to get the signed url from cache');
    }
    return cacheItem;
  } catch (err) {
    console.log(err);
    throw new Error('failed to obtain signed url');
  }
};
export const uploadPostFile = (
  url: string,
  file: Blob,
  fields: { append: (k: string, v: string | Blob) => void },
  setProgress?: (p: number) => void
) => {
  fields.append('file', file);
  return axios.post(url, fields, {
    onUploadProgress: p => {
      if (setProgress) {
        setProgress(Math.round((p.loaded / p.total) * 100));
      }
    },
  });
};

export const getFullUSDate = data => {
  return new Date(data).toLocaleString('en-US', {
    month: 'numeric',
    year: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  });
};

export const getUSDate = data => {
  return new Date(data).toLocaleString('en-US', {
    month: 'numeric',
    year: 'numeric',
    day: 'numeric',
  });
};

export const DEFAULT_SORT_TYPE = 'Last Edited';
export const DEFAULT_VIEW_TYPE = 'grid';

export const scrollFeed = (coordY: number) => {
  const player = document.getElementById('feed-player');
  if (player) {
    player.scrollTo(0, coordY - 40);
  }
};

export const scrollOnUpload = (
  type,
  feedcardFrom,
  feedcardFromoffsetHeight,
  feedcardFromScrollTop
) => {
  if (type === 'profile' || type === 'graphic') {
    setTimeout(() => feedcardFrom?.scrollTo(0, 0), 10);
  } else if (type === 'resource') {
    setTimeout(
      () => feedcardFrom?.scrollTo(0, feedcardFromoffsetHeight as number),
      10
    );
  } else {
    setTimeout(
      () => feedcardFrom?.scrollTo(0, feedcardFromScrollTop as number),
      10
    );
  }
};

export const getGiideSettingsTooltipMessage = (
  activeTab: SettingsTabKeys,
  useTemplate: boolean,
  giideData: SettingsData,
  imageEditorValidation: ImageEditorValidation
): string | undefined => {
  switch (activeTab) {
    case SettingsTabKeys.image:
      if (!useTemplate && !giideData.cardDetails.thumbnailCustomPath) {
        return 'Please add a thumbnail image';
      }

      if (
        !giideData.cardDetails.useCustomThumbnail &&
        !giideData.cardDetails.thumbnailImg
      ) {
        return 'Please choose a thumbnail image';
      }

      if (imageEditorValidation.failed) {
        return imageEditorValidation.errorMessage;
      }

      return '';
    case SettingsTabKeys.details:
      return !giideData.title
        ? 'Please fill in title of Giide'
        : !giideData.speaker.fullName
        ? 'Please fill in the author name'
        : // : !giideData.speaker.title
          // ? 'Please fill in the author title'
          // : !giideData.description
          // ? 'Please fill in summary'
          '';
    default:
      return '';
  }
};

export const isLocalhost: boolean =
  process.env.REACT_APP_LOCALHOST === 'true' || false;

export const redirectToLogin = push => {
  // return;
  if (isLocalhost) {
    push('/login');
  } else {
    window.location.assign(`${window.location.origin}/signin`);
  }
};

export const BASE_URL =
  process.env.REACT_APP_BASE_URL || 'https://www-development.giide.com';

export const publishClicked = (
  setModalContent,
  ModalContent,
  dispatch,
  giide,
  haveAnyCardsAttached,
  modalHookAPI: HookAPI
) => {
  const { steps, ...giideWithoutSteps } = giide;

  const openModal = () => {
    setModalContent(ModalContent.publish);
    batch(() => {
      dispatch(setGiideContentModal(ModalContent.publish));
      dispatch(setEditingChapterId(undefined));
      dispatch(setCardProps());
      dispatch(setOldGiideData(giideWithoutSteps as SettingsData));
    });

    ReactGA.event({
      category: 'Button',
      action: 'Preview & Publish Button Pressed',
    });
  };

  if (!haveAnyCardsAttached) {
    modalHookAPI.confirm({
      centered: true,
      closable: true,
      cancelText: 'Cancel',
      okText: 'Publish',
      onCancel: () => false,
      onOk: async () => {
        openModal();
        return true;
      },
      title: 'Publish Giide',
      content:
        "Are you sure you're ready to publish? You haven't attached any content to your giide yet.",
    });
  } else {
    openModal();
  }
};

export const previewClicked = async loadPreview => {
  const previewLoaded = await loadPreview(true, true);
  if (previewLoaded) {
    ReactGA.event({
      category: 'Button',
      action: 'Preview Button Pressed',
    });
  }
};

export const REFRESH_AUDIO_LIB_TIME = 7 * 1000; // value in milliseconds => 7s

export enum FeedcardType {
  profile = 'profile',
  highlight = 'highlight',
  resource = 'link',
  graphic = 'image',
  definition = 'definition',
  activity = 'activity',
  quote = 'quote',
  list = 'list',
  quiz = 'quiz',
  poll = 'poll',
  factoid = 'factoid',
  portrait = 'portrait',
  welcome = 'welcome',
  video = 'video',
  text = 'text',
}

export const getCanvasElementById = (id: string): HTMLCanvasElement | null => {
  const canvas = document.getElementById(id);
  if (!(canvas instanceof HTMLCanvasElement)) {
    return null;
  }
  return canvas;
};

export const haveGiideChapters = (giide, user) => {
  return !!(
    giide?.hasChapters ||
    (giide?.hasChapters === null &&
      user?.plan.features?.chapters?.allowChaptersWithExistingGiides)
  );
};

export const blobToBase64 = blob => {
  const reader = new FileReader();
  reader.readAsDataURL(blob);
  return new Promise(resolve => {
    reader.onloadend = () => {
      resolve(reader.result);
    };
  });
};

export const gotMicPermissions = () => {
  return new Promise<boolean>((resolved, rejected) => {
    let timeout = setTimeout(() => {
      resolved(false);
    }, 500);
    navigator.mediaDevices
      .getUserMedia({
        audio: true,
        video: false,
      })
      .then(() => {
        clearTimeout(timeout);
        resolved(true);
      })
      .catch(() => {
        resolved(false);
      })
      .finally(() => {
        resolved(false);
      });
  });
};

export const getAudioBufferFromBlob = (recordedBlob: Blob, audioContext) => {
  return new Promise<AudioBuffer>((resolved, rejected) => {
    try {
      const reader = new FileReader();
      reader.onloadend = e => {
        audioContext.decodeAudioData(
          e.target?.result as ArrayBuffer,
          async buffer => {
            resolved(buffer);
          },
          e => {
            rejected(new Error('Failed to decode audio data'));
          }
        );
      };
      reader.readAsArrayBuffer(recordedBlob);
    } catch {
      rejected(new Error('Failed to decode audio data'));
    }
  });
};

export const concatTwoAudioBuffers = (
  buffer1: AudioBuffer,
  buffer2: AudioBuffer,
  audioContext
) => {
  try {
    const numberOfChannels = Math.min(
      buffer1.numberOfChannels,
      buffer2.numberOfChannels
    );
    const tmpBuffer = audioContext.createBuffer(
      numberOfChannels,
      buffer1.length + buffer2.length,
      buffer1.sampleRate
    );
    for (var i = 0; i < numberOfChannels; i++) {
      const channel = tmpBuffer.getChannelData(i);
      channel.set(buffer1.getChannelData(i), 0);
      channel.set(buffer2.getChannelData(i), buffer1.length);
    }
    return tmpBuffer;
  } catch (e) {
    throw new Error('Failed to concat recordings');
  }
};

export const calculatePlayerTimeSeek = (
  delta: number,
  progressTime: number,
  totalDuration: number
) => {
  // workaround for playing start playing again after seeking at the end, even when playing is false.
  const adjustedTime = progressTime + delta;
  let seekTo;
  if (adjustedTime <= 0) {
    seekTo = 0;
  } else if (adjustedTime < totalDuration * 1000) {
    seekTo = adjustedTime;
  } else {
    // -25 ms to be sure that it doesnt overflow while playing
    // note: -1ms could possibly work too without issue, but 25 is better
    // because there is 20ms interval on player, so its more safe to have some space
    seekTo = totalDuration * 1000 - 25;
  }
  return seekTo;
};
